import { useQuery } from '@apollo/client';
import { gql } from '__generated__';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

import IdentityLayout from 'layouts/IdentityLayout';

import Button from 'components/Button';
import DetailsPanel from 'components/DetailsPanel';
import ErrorMessage from 'components/ErrorMessage';
import LoadingIndicator from 'components/LoadingIndicator';

import UserDocuments from './Documents';
import downloadVCF from './contact-generator';
import theme from './theme.module.scss';

const IDENTITY_CARD_USER_QUERY = gql(`
  query IdentityCardUser($id: ID!) {
    identityCard(id: $id) {
      id
      user {
        id
        fullName
        firstName
        middleName
        lastName
        email
        phoneNumber
        gender
        dateOfBirth
        employee {
          id
          fileNumber
          organisationCode
          workEmail
          workPhoneNumber
          signature
          photo
          designation {
            name
          }
          division {
            name
          }
          address {
            id
            line1
            line2
            city
            province
            postalCode
            country
          }
        }
        department {
          id
          name
          shortName
          logo
        }
        createdAt
      }
    }
  }
`);

const UserPublicInformation: FC = () => {
  const { identityCardId } = useParams<{ identityCardId: string }>() as { identityCardId: string };

  const { loading, error, data, refetch } = useQuery(IDENTITY_CARD_USER_QUERY, {
    variables: {
      id: identityCardId,
    },
  });

  function renderContent() {
    if (loading) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const user = data.identityCard.user;
    const employee = user.employee;
    const isNHC = user.department.id === 'a2a13993-2b58-4833-a702-5b582d3e5f72';

    return (
      <>
        <div className={theme.logoContainer}>
          <img
            className={theme.logo}
            src={user.department.logo as string}
            alt={`${user.department.shortName} logo`}
            height={80}
          />
        </div>
        <div className={theme.container}>
          <div className={theme.identityInformation}>
            <img className={theme.photo} src={employee?.photo as string} alt="Employee" />
            <div>
              <p className={theme.fullName}>{user.fullName}</p>
              <p className={theme.role}>{employee?.designation.name}</p>
              {user.department.name.includes('Education') && (
                <p className={theme.division}>{employee?.division.name}</p>
              )}
            </div>
          </div>
          <DetailsPanel
            title="Identity Information"
            data={[
              { label: 'First Name', value: user.firstName },
              { label: 'Last Name', value: user.lastName },
              { label: 'File Number', value: employee?.fileNumber },
              ...(isNHC ? [] : [{ label: 'Organisation', value: employee?.organisationCode }]),
              { label: 'Department', value: user.department.name },
              ...(isNHC ? [{ label: 'Division', value: employee?.division.name }] : []),
              { label: 'Designation', value: employee?.designation.name },
            ]}
          />
          <DetailsPanel
            title="Business Information"
            data={[
              { label: 'Phone Number', value: user.phoneNumber },
              { label: 'Email', value: user.email },
              {
                label: 'Address',
                value: `${employee?.address?.line1}, ${
                  employee?.address?.line2 ? `${employee?.address?.line2},` : ''
                } ${employee?.address?.city}`,
              },
              { label: 'Province', value: employee?.address?.province },
              { label: 'Country', value: employee?.address?.country, type: 'COUNTRY' },
            ]}
          />
          <Button onClick={() => downloadVCF(user.fullName, user.phoneNumber || '', user.email)}>
            Add to Contacts
          </Button>
          <UserDocuments userId={user.id as string} identityCardId={identityCardId} />
        </div>
      </>
    );
  }

  return <IdentityLayout>{renderContent()}</IdentityLayout>;
};

export default UserPublicInformation;
